import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-fc5afb62"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "signable-documents-modal-content" }
const _hoisted_2 = { class: "date-cell" }

import { computed, ref, inject, watch, PropType } from "vue";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import GridBase from "@/components/ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import { SignableListItem } from "@/types/signable-documents/SignableListItem";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import GridFilterSummary from "@/components/ui/grid/grid-filter-summary.vue";
import { SignableListFilters } from "@/types/signable-documents/SignableListFilters";
import SignableDocumentsFilterModal from "@/components/signable-documents/signable-documents-filter-modal.vue";
import SignableDocumentHistoryModal from "@/components/signable-documents/signable-document-history-modal.vue";
import { GridFilter } from "@/types/ui/GridFilter";
import { formatDate } from "bright-logic-core/src/format";
import { DxToolbarItem } from "devextreme-vue/popup";
import { SignableListAPIFilters } from "@/types/signable-documents/SignableListAPIFilters";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";
import EditTaskModal from "@/components/tasks/edit-task-modal.vue";
import { ContactDocumentDownloader } from "@/utils/correspondence/ContactDocumentDownloader";
import { useUserPermissionsStore } from "@/stores/UserPermissionsStore";
import { UserPermissions } from "@/types/user/permissions/UserPermissions";
import { ReminderFilters } from "@/types/reminders/ReminderFilters";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
//@ts-ignore
import { debounce } from "lodash-es";


export default /*#__PURE__*/_defineComponent({
  __name: 'returned-signable-documents-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The filters from the reminders page are passed to the modal to be the default filters.
   * @type {object}
   * @required
   */
  reminderPageFilters: {
    type: Object as PropType<ReminderFilters>,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes.
   * Used to sync the modal open/close state with the parent component via v-model.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Triggered when all returned documents are successfully accepted.
   * Notifies the parent component to refresh or update its state.
   */
  "acceptAllUpdated",
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const apiNextClient = inject("AcquaintApiNextClient") as AcquaintApiNextClient;
const userPermissionsStore = useUserPermissionsStore();
const userPermissions = ref<UserPermissions>();
const dataLoading = ref(false);
const signableDocuments = ref<SignableListItem[]>([]);
const rowCount = computed(() => {
  return signableDocuments.value.length;
});
const filters = ref<SignableListFilters>({
  branches: [],
  users: [],
});
const selectedDocumentRecord = ref<SignableListItem>();
const showFiltersModal = ref(false);
const openTaskModal = ref(false);
const openHistoryModal = ref(false);
const apiFilters = computed(() => {
  const newFilters = new SignableListAPIFilters();

  if (filters.value) {
    newFilters.branchs = filters.value.branches?.map((item) => item.id);
    newFilters.users = filters.value.users?.map((item) => item.id);

    newFilters.branchs = newFilters.branchs?.filter((item) => {
      return item != -1;
    });
    newFilters.users = newFilters.users?.filter((item) => {
      return item != -1;
    });
  }

  return newFilters;
});

// Debounced fetch function to prevent multiple rapid calls
const fetchSignableDocuments = debounce(async () => {
  if (!showModal.value) return;
  dataLoading.value = true;
  try {
    signableDocuments.value = await apiNextClient.httpPost(
      "SignableDocuments/GetReturnedDocuments",
      apiFilters.value,
    );
  } catch (error) {
    signableDocuments.value = [];
    displayErrorMessage(getErrorMessage(error));
  } finally {
    selectedDocumentRecord.value = undefined;
    dataLoading.value = false;
  }
}, 300);

// Filter code
watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      signableDocuments.value = [];
      return;
    }

    // Set filters without triggering redundant reactivity
    filters.value = {
      branches: [props.reminderPageFilters.branch].filter(Boolean),
      users: [props.reminderPageFilters.user].filter(Boolean),
    };

    userPermissions.value = await userPermissionsStore.getUserPermissions();
    fetchSignableDocuments();
  },
);

const summaryFilters = computed(() => {
  const gridFilters: GridFilter[] = [];

  // No filters so return blank array so summary shows All
  if (!filters.value) {
    return [];
  }

  if (filters.value.branches.length > 0) {
    gridFilters.push({
      title: "Branches",
      values: filters.value.branches,
      dataSource: [],
    });
  }

  if (filters.value.users.length > 0) {
    gridFilters.push({
      title: "Users",
      values: filters.value.users,
      dataSource: [],
    });
  }

  return gridFilters;
});

// When filters are changed refresh data in the grid
watch(
  apiFilters,
  async () => {
    await fetchSignableDocuments();
  },
  { deep: true },
);

const updateFilters = (newFilters: SignableListFilters) => {
  filters.value = { ...newFilters };
};

const onFilterClick = () => (showFiltersModal.value = true);

const menuItems = computed(() => {
  const menu: ContextMenuItem[] = [];

  if (userPermissions.value?.correspondence.new) {
    menu.push({
      text: "Create Task",
      key: "createTask",
      icon: "",
      disabled: !selectedDocumentRecord.value,
    });
  }

  menu.push(
    {
      text: "View Progress/History",
      key: "history",
      icon: "",
      disabled: !selectedDocumentRecord.value,
    },
    {
      text: "Refresh",
      key: "refresh",
      icon: "",
    },
  );

  return menu;
});

const onMenuItemClick = (key: string) => {
  switch (key) {
    case "createTask":
      openTaskModal.value = true;
      break;
    case "history":
      openHistoryModal.value = true;
      break;
    case "refresh":
      fetchSignableDocuments();
      break;
  }
};

// Single row click
const onDocumentSelected = (document: SignableListItem) => {
  if (!document) {
    return;
  }

  selectedDocumentRecord.value = { ...document };
};

//Double row click
const viewDocument = async (document: SignableListItem) => {
  if (!document) {
    return;
  }

  selectedDocumentRecord.value = { ...document };
  const fileDownloader = new ContactDocumentDownloader(apiNextClient);
  await fileDownloader.download(selectedDocumentRecord.value.documentId);
};
const getUserIds = () => {
  return [...new Set(signableDocuments.value.map((item) => item.userId))];
};
const acceptAllClick = async () => {
  const userIds = getUserIds() ?? [];

  try {
    await apiNextClient.httpPost("SignableDocuments/AcceptReturnedDocuments", {
      users: userIds,
    });
    showModal.value = false;
    emit("acceptAllUpdated");
  } catch (error) {
    displayErrorMessage(getErrorMessage(error));
  }
};

// Cancel button click
const cancelClick = () => {
  showModal.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
      title: "Returned Signable Documents",
      height: "600px",
      width: _unref(ModalWidth).Large,
      onCancel: cancelClick
    }, {
      "non-scrollable-content": _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(GridFilterSummary, {
            "menu-items": menuItems.value,
            onMenuItemClick: onMenuItemClick,
            filters: summaryFilters.value,
            showGenericMenuItems: false,
            dropdownMenuDisable: false,
            "row-count": rowCount.value,
            onFilterClick: onFilterClick,
            gridMenuId: "returned-signable-documents-menu"
          }, null, 8, ["menu-items", "filters", "row-count"]),
          _createVNode(GridBase, {
            "data-source": signableDocuments.value,
            "id-column-name": "synckey",
            customColumnSlots: ['dateCell'],
            wordWrapEnabled: true,
            onRowSelected: onDocumentSelected,
            loading: dataLoading.value,
            onRowClick: viewDocument,
            onAdaptiveRowSelection: onDocumentSelected
          }, {
            columns: _withCtx(() => [
              _createVNode(_unref(DxColumn), {
                "data-field": "date",
                caption: "Date",
                "hiding-priority": 4,
                "cell-template": "dateCell",
                width: "100px"
              }),
              _createVNode(_unref(DxColumn), {
                "data-field": "description",
                caption: "Document",
                "hiding-priority": 5,
                width: "315px"
              }),
              _createVNode(_unref(DxColumn), {
                "data-field": "contact",
                caption: "Contact",
                "hiding-priority": 3,
                width: "200px"
              }),
              _createVNode(_unref(DxColumn), {
                "data-field": "property",
                caption: "Property",
                "hiding-priority": 2,
                width: "200px"
              }),
              _createVNode(_unref(DxColumn), {
                "data-field": "user",
                caption: "User",
                "hiding-priority": 1,
                width: "153px"
              })
            ]),
            dateCellData: _withCtx(({ data }) => [
              _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(formatDate)(data.date)), 1)
            ]),
            _: 1
          }, 8, ["data-source", "loading"])
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Accept All',
          onClick: () => {
            acceptAllClick();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "width"]),
    _createVNode(SignableDocumentsFilterModal, {
      modelValue: showFiltersModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showFiltersModal).value = $event)),
      onFiltersApplied: updateFilters,
      reminderPageFilters: props.reminderPageFilters,
      modalTitle: 'Returned Signable Documents Filters'
    }, null, 8, ["modelValue", "reminderPageFilters"]),
    _createVNode(EditTaskModal, {
      modelValue: openTaskModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((openTaskModal).value = $event)),
      id: 0,
      propertyID: selectedDocumentRecord.value?.propertyId,
      contactID: selectedDocumentRecord.value?.contactId
    }, null, 8, ["modelValue", "propertyID", "contactID"]),
    (selectedDocumentRecord.value)
      ? (_openBlock(), _createBlock(SignableDocumentHistoryModal, {
          key: 0,
          modelValue: openHistoryModal.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((openHistoryModal).value = $event)),
          synckey: selectedDocumentRecord.value?.synckey
        }, null, 8, ["modelValue", "synckey"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})